// ── Manifest ──

export interface ExtensionPoint {
  type: string;
  label: string;
  activePages: string[];
}

export interface FrontendModule {
  name: string;
  displayName: string;
  description: string;
  exportName: string;
}

export interface Manifest {
  version: string;
  permissions: string[];
  webhookEvents: string[];
  metafieldNamespaces: string[];
  extensions: ExtensionPoint[];
  frontendModules: FrontendModule[];
}

// ── Marketplace App ──

export interface Pricing {
  id: string;
  type: string;
  priceAmountCents: number;
  currency: string;
  trialDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  body: string;
  createdAt: string;
}

export interface Stats {
  totalInstallations: number;
  averageRating: number | null;
  totalReviews: number;
}

export interface App {
  id: string;
  organizationAppId: string;
  name: string;
  shortDescription: string;
  description: string;
  icon: string | null;
  screenshots: string[];
  categories: string[];
  version: string;
  status: string;
  manifest: Manifest;
  pricing: Pricing | null;
  stats: Stats;
  reviews: { nodes: Review[]; totalCount: number };
  privacyPolicyUrl: string | null;
  supportUrl: string | null;
  websiteUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Organization App (OAuth2 / Webhooks / Releases) ──

export interface OrgAppConfigMetadata {
  loginUrl: string;
  consentUrl: string;
  public: boolean;
  requiresConsent: boolean;
}

export interface OrgAppConfig {
  name: string;
  redirectUrls: string[];
  logoutRedirectUrls: string[];
  scope: string;
  metadata: OrgAppConfigMetadata;
}

export interface AppWebhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  organizationId: string;
  installationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewCheck {
  name: string;
  passed: boolean;
  details: string | null;
}

export interface ReviewResult {
  passed: boolean;
  checks: ReviewCheck[];
}

export interface OrgAppRelease {
  id: string;
  version: string;
  status: string;
  notes: string | null;
  manifest: Manifest;
  reviewResult: ReviewResult | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrgApp {
  id: string;
  name: string;
  description: string;
  shortDescription: string | null;
  icon: string | null;
  public: boolean | null;
  creatorId: string;
  creator: { id: string; email: string };
  configuration: OrgAppConfig;
  appWebhooks: AppWebhook[];
  releases: OrgAppRelease[];
  createdAt: string;
  updatedAt: string;
}

// ── Marketplace Releases (with bundle info) ──

export interface MarketplaceRelease {
  id: string;
  version: string;
  status: string;
  releaseNotes: string | null;
  extensionBundleKey: string | null;
  moduleBundleKey: string | null;
  manifest: {
    version: string;
    permissions: string[];
    frontendModules: { name: string; displayName: string }[];
    extensions: { type: string; label: string; activePages: string[] }[];
  };
  reviewResult: ReviewResult | null;
  reviewedAt: string | null;
  createdAt: string;
}

// ── Helpers ──

export const STATUS_COLORS: Record<string, string> = {
  PUBLISHED: "green",
  DRAFT: "gray",
  IN_REVIEW: "yellow",
  REJECTED: "red",
  SUSPENDED: "red",
};

export const RELEASE_STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: "yellow",
  APPROVED: "green",
  REJECTED: "red",
  PUBLISHED: "green",
  DRAFT: "gray",
};

export function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}
